/**
 * Owner: Masa Bayu
 * File: commerce-order.ts
 * Date:Time: 2026-09-15 22:25
 * Description: T3 pending order draft — E1 create; E2 payment attempt link/retry.
 */
import { assertNoForbiddenFoundationKey } from './authority';
import { assertT3E1SecurityIntact } from './commerce-security';
import { createQxreadId } from './id';
import { assertTimestamp, assertValidId } from './guards';
export const QXREAD_ORDER_STATES = ['pending', 'cancelled'];
export function createPendingOrderDraft(checkout, lineSnapshots, options) {
    assertNoForbiddenFoundationKey(options ?? {}, 'QXRead order draft');
    assertT3E1SecurityIntact();
    if (checkout.state !== 'ready' || checkout.totals === null) {
        throw new Error('QXRead pending order requires a ready checkout with totals');
    }
    if (lineSnapshots.length === 0) {
        throw new Error('QXRead pending order requires lines');
    }
    const orderId = options?.orderId !== undefined ? options.orderId : createQxreadId('qxrdord');
    assertValidId(orderId, 'QXRead orderId');
    const createdAt = options?.createdAt ?? new Date().toISOString();
    assertTimestamp(createdAt, 'QXRead order createdAt');
    return Object.freeze({
        orderId,
        checkoutId: checkout.checkoutId,
        cartId: checkout.cartId,
        state: 'pending',
        lines: Object.freeze(lineSnapshots.map((line) => Object.freeze({
            publicationId: line.publicationId,
            title: line.title,
            quantity: line.quantity,
            sellerPriceMinorUnits: line.sellerPriceMinorUnits,
            currency: line.currency,
        }))),
        totals: checkout.totals,
        createdAt,
        paymentAttemptId: null,
        entitlementId: null,
    });
}
/** E1 invariant — pending order never carries payment or entitlement ids. */
export function isOrderDraftPaymentSafe(order) {
    return (order.state === 'pending' && order.paymentAttemptId === null && order.entitlementId === null);
}
/** Link a payment attempt to a pending order before provider callbacks. */
export function linkOrderToPaymentAttempt(order, paymentAttemptId) {
    if (order.state !== 'pending') {
        throw new Error('QXRead payment link requires a pending order');
    }
    if (order.entitlementId !== null) {
        throw new Error('QXRead payment link blocked — entitlement already present');
    }
    if (order.paymentAttemptId !== null && order.paymentAttemptId !== paymentAttemptId) {
        throw new Error('QXRead payment link blocked — order already linked to another attempt');
    }
    assertValidId(paymentAttemptId, 'QXRead paymentAttemptId');
    return Object.freeze({
        ...order,
        paymentAttemptId,
        entitlementId: null,
    });
}
/**
 * After a failed payment attempt, clear the link so a new intent can begin.
 * Cancelled orders are not recoverable — create a new pending order instead.
 */
export function prepareOrderForPaymentRetry(order, failedAttemptId) {
    if (order.state !== 'pending') {
        throw new Error('QXRead payment retry requires a pending order');
    }
    if (order.paymentAttemptId !== failedAttemptId) {
        throw new Error('QXRead payment retry requires the failed attempt id currently linked');
    }
    if (order.entitlementId !== null) {
        throw new Error('QXRead payment retry blocked — entitlement already present');
    }
    return Object.freeze({
        ...order,
        paymentAttemptId: null,
        entitlementId: null,
    });
}
//# sourceMappingURL=commerce-order.js.map